import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Typography,
  LinearProgress,
  Tooltip,
  Collapse,
  Alert,
  Stack,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Replay as ReplayIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';

const ChunksList = ({ jobId, isAdmin = false }) => {
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [retrying, setRetrying] = useState({});

  useEffect(() => {
    if (jobId) {
      fetchChunks();
    }
  }, [jobId]);

  const fetchChunks = async () => {
    setLoading(true);
    try {
      const chunks = await apiService.getAudioJobChunks(jobId);
      setChunks(chunks);
    } catch (error) {
      console.error('Error fetching chunks:', error);
      toast.error('Failed to load chunks');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryChunk = async (chunkId, chunkIndex) => {
    setRetrying({ ...retrying, [chunkId]: true });
    try {
      const response = await apiService.retryAudioChunk(chunkId);
      toast.success(`Chunk ${chunkIndex} retry: ${response.message}`);
      
      // Refresh chunks after retry
      await fetchChunks();
    } catch (error) {
      console.error('Error retrying chunk:', error);
      toast.error(error.response?.data?.detail || 'Failed to retry chunk');
    } finally {
      setRetrying({ ...retrying, [chunkId]: false });
    }
  };

  const handleRetryAllFailed = async () => {
    try {
      const response = await apiService.retryAllFailedChunks(jobId);
      toast.success(response.message);
      
      // Refresh chunks
      await fetchChunks();
    } catch (error) {
      console.error('Error retrying failed chunks:', error);
      toast.error('Failed to retry failed chunks');
    }
  };

  const toggleExpanded = (chunkId) => {
    setExpanded({
      ...expanded,
      [chunkId]: !expanded[chunkId]
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      case 'retry_requested':
        return 'info';
      default:
        return 'default';
    }
  };

  const getQualityColor = (score) => {
    if (!score) return 'default';
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (!isAdmin) {
    return null;
  }

  const failedChunks = chunks.filter(chunk => chunk.status === 'failed');
  const hasQualityIssues = chunks.some(chunk => chunk.quality_score && chunk.quality_score < 0.6);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Audio Chunks Analysis (Admin)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {failedChunks.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleRetryAllFailed}
                startIcon={<ReplayIcon />}
              >
                Retry All Failed ({failedChunks.length})
              </Button>
            )}
            <IconButton onClick={fetchChunks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {hasQualityIssues && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some chunks have quality issues. Consider retrying chunks with quality scores below 0.6.
          </Alert>
        )}

        {loading ? (
          <LinearProgress />
        ) : chunks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No chunks found for this job.
          </Typography>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Chunks
                    </Typography>
                    <Typography variant="h4">
                      {chunks.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Completed
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {chunks.filter(c => c.status === 'completed').length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Failed
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {failedChunks.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Avg Quality
                    </Typography>
                    <Typography variant="h4">
                      {(chunks.reduce((sum, c) => sum + (c.quality_score || 0), 0) / chunks.length).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Chunks Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Chunk</TableCell>
                    <TableCell>Time Range</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Quality</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Retries</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chunks.map((chunk) => (
                    <React.Fragment key={chunk.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(chunk.id)}
                          >
                            {expanded[chunk.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            Chunk {chunk.chunk_index}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDuration(chunk.start_time)} - {formatDuration(chunk.end_time)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={chunk.status}
                            color={getStatusColor(chunk.status)}
                            size="small"
                            icon={
                              chunk.status === 'completed' ? <CheckCircleIcon /> :
                              chunk.status === 'failed' ? <ErrorIcon /> :
                              chunk.status === 'processing' ? <RefreshIcon /> :
                              <WarningIcon />
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {chunk.quality_score ? (
                            <Chip
                              label={`${(chunk.quality_score * 100).toFixed(0)}%`}
                              color={getQualityColor(chunk.quality_score)}
                              size="small"
                            />
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>{formatFileSize(chunk.file_size_bytes)}</TableCell>
                        <TableCell>
                          {chunk.retry_count > 0 && (
                            <Chip
                              label={`${chunk.retry_count}/${chunk.max_retries || 3}`}
                              size="small"
                              color={chunk.retry_count >= 3 ? 'error' : 'default'}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {chunk.translated_chunk_url && (
                              <Tooltip title="Download translated chunk">
                                <IconButton
                                  size="small"
                                  href={chunk.translated_chunk_url}
                                  target="_blank"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {chunk.original_chunk_url && (
                              <Tooltip title="Play original">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(chunk.original_chunk_url, '_blank')}
                                >
                                  <PlayIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(chunk.status === 'failed' || chunk.status === 'completed') && 
                             chunk.retry_count < (chunk.max_retries || 3) && (
                              <Tooltip title="Retry this chunk">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRetryChunk(chunk.id, chunk.chunk_index)}
                                  disabled={retrying[chunk.id]}
                                  color="primary"
                                >
                                  <ReplayIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expanded[chunk.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Chunk Details
                                  </Typography>
                                  <Stack spacing={1}>
                                    <Typography variant="body2">
                                      <strong>Duration:</strong> {chunk.duration}s
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Validated:</strong> {chunk.validated ? 'Yes' : 'No'}
                                    </Typography>
                                    {chunk.elevenlabs_dubbing_id && (
                                      <Typography variant="body2">
                                        <strong>Provider Job ID:</strong> {chunk.elevenlabs_dubbing_id}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                  {chunk.error_message && (
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                      {chunk.error_message}
                                    </Alert>
                                  )}
                                  {chunk.retry_reasons.length > 0 && (
                                    <>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Retry History
                                      </Typography>
                                      <Stack spacing={0.5}>
                                        {chunk.retry_reasons.map((reason, index) => (
                                          <Typography key={index} variant="body2" color="text.secondary">
                                            • {reason}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ChunksList;